import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2, X, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  program_id: '',
  period_months: 1,
  label: '',
  price_usd: 0,
  price_cop: 0,
  discount_percent: 0,
  is_active: true,
  order: 0,
});

const AdminSubscriptionPricing = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/subscription-pricing`, { headers });
      setRows(res.data || []);
    } catch (err) {
      toast.error('No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (row) => {
    setForm({
      program_id: row.program_id || '',
      period_months: row.period_months || 1,
      label: row.label || '',
      price_usd: row.price_usd || 0,
      price_cop: row.price_cop || 0,
      discount_percent: row.discount_percent || 0,
      is_active: row.is_active !== false,
      order: row.order || 0,
    });
    setEditingId(row.pricing_id || row._id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        program_id: form.program_id.trim(),
        period_months: parseInt(form.period_months) || 1,
        label: form.label.trim(),
        price_usd: parseFloat(form.price_usd) || 0,
        price_cop: parseFloat(form.price_cop) || 0,
        discount_percent: parseFloat(form.discount_percent) || 0,
        is_active: !!form.is_active,
        order: parseInt(form.order) || 0,
      };
      if (editingId) {
        await axios.patch(`${BACKEND_URL}/api/admin/subscription-pricing/${editingId}`, payload, { headers });
        toast.success('Periodo actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/subscription-pricing`, payload, { headers });
        toast.success('Periodo creado');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm());
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/subscription-pricing/${row.pricing_id || row._id}`,
        { is_active: !row.is_active },
        { headers }
      );
      fetchRows();
    } catch (err) {
      toast.error('No se pudo actualizar');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('¿Eliminar este periodo de pago?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/subscription-pricing/${row.pricing_id || row._id}`, { headers });
      toast.success('Periodo eliminado');
      fetchRows();
    } catch (err) {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin" className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm mb-2">
              <ArrowLeft className="w-4 h-4" /> Volver
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <DollarSign className="w-7 h-7 text-emerald-400" />
              Pagos por Periodo
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Gestiona intervalos (1, 2, 3, 6, 12 meses) y precios de suscripción
            </p>
          </div>
          <Button
            onClick={startCreate}
            className="bg-emerald-600 hover:bg-emerald-500 rounded-full"
            data-testid="pricing-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Nuevo periodo
          </Button>
        </div>

        {showForm && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingId ? 'Editar' : 'Nuevo'} Periodo</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-300">Program ID</label>
                <input
                  value={form.program_id}
                  onChange={(e) => setForm({ ...form, program_id: e.target.value })}
                  placeholder="ej: premium, body_reset, get_bigger"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 mt-1"
                  data-testid="pricing-form-program-id"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300">Meses del periodo</label>
                <select
                  value={form.period_months}
                  onChange={(e) => setForm({ ...form, period_months: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 mt-1"
                  data-testid="pricing-form-period-months"
                >
                  {[1, 2, 3, 6, 12].map(m => (
                    <option key={m} value={m}>{m} {m === 1 ? 'mes' : 'meses'}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-zinc-300">Etiqueta visible</label>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="ej: Mensual, Anual, Semestral"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 mt-1"
                  data-testid="pricing-form-label"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300">Precio USD</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.price_usd}
                  onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 mt-1"
                  data-testid="pricing-form-price-usd"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300">Descuento %</label>
                <input
                  type="number"
                  step="1"
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 mt-1"
                  data-testid="pricing-form-discount"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300">Orden</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 mt-1"
                  data-testid="pricing-form-order"
                />
              </div>
              <label className="flex items-center gap-2 md:col-span-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="pricing-form-active"
                />
                <span>Activo (visible en checkout)</span>
              </label>
              <div className="md:col-span-2 flex gap-3">
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500 rounded-full" data-testid="pricing-form-save">
                  <Save className="w-4 h-4 mr-1" /> Guardar
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full border-zinc-700 text-zinc-300">
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <p className="text-zinc-500">Cargando...</p>
        ) : rows.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 text-center" data-testid="pricing-empty-state">
            <p className="text-zinc-400">No hay periodos configurados.</p>
            <p className="text-zinc-500 text-xs mt-2">
              La colección <code className="text-emerald-400">subscription_pricing_periods</code> vive en la base de datos de producción.
              En preview aparece vacía, esto es esperado.
            </p>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
            <table className="w-full text-sm" data-testid="pricing-table">
              <thead className="bg-zinc-800 text-zinc-400">
                <tr>
                  <th className="text-left p-3">Programa</th>
                  <th className="text-left p-3">Periodo</th>
                  <th className="text-left p-3">Etiqueta</th>
                  <th className="text-right p-3">USD</th>
                  <th className="text-right p-3">COP</th>
                  <th className="text-right p-3">Desc%</th>
                  <th className="text-center p-3">Activo</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.pricing_id || row._id} className="border-t border-zinc-800">
                    <td className="p-3">{row.program_id}</td>
                    <td className="p-3">{row.period_months} {row.period_months === 1 ? 'mes' : 'meses'}</td>
                    <td className="p-3">{row.label}</td>
                    <td className="p-3 text-right">${Number(row.price_usd || 0).toFixed(2)}</td>
                    <td className="p-3 text-right">${Number(row.price_cop || 0).toLocaleString('es-CO')}</td>
                    <td className="p-3 text-right">{row.discount_percent || 0}%</td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => toggleActive(row)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${row.is_active ? 'bg-emerald-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}
                        data-testid={`pricing-toggle-${row.pricing_id || row._id}`}
                      >
                        {row.is_active ? 'ON' : 'OFF'}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => startEdit(row)} className="text-emerald-400 hover:text-emerald-300 mr-3 text-xs" data-testid={`pricing-edit-${row.pricing_id || row._id}`}>
                        Editar
                      </button>
                      <button onClick={() => handleDelete(row)} className="text-red-400 hover:text-red-300" data-testid={`pricing-delete-${row.pricing_id || row._id}`}>
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscriptionPricing;
